import { BookingStatus, TRANSITIONS } from '../../../../util/enums';
import { getBookingStatusAppearance } from '../../../../util/bookingStatusAppearance';
import { getStatusByTransition } from '../../../InboxPage/sections/useProcessedBookings';
import {
  getNotificationTransitionName,
  isCreativeAcceptedPendingPaymentNotification,
  isExpiredRequestNotification,
  isHostPayoutNotification,
  isPaymentSuccessNotification,
  isPaymentWindowPassedNotification,
  isProviderNotificationTarget,
} from './notificationEventMeta';

const MESSAGE_NOTIFICATION_APPEARANCE = Object.freeze({
  color: '#4F46E5',
  softColor: '#EEF2FF',
  status: 'message',
});

const CANCELLED_ICON_STATUSES = new Set([
  BookingStatus.CANCELLED,
  BookingStatus.DECLINED,
  BookingStatus.RESCHEDULE_DECLINED,
]);

const COMPLETED_TRANSITIONS = new Set([
  TRANSITIONS.COMPLETE,
  TRANSITIONS.OPERATOR_COMPLETE,
  TRANSITIONS.COMPLETE_AFTER_CANCEL,
  TRANSITIONS.OPERATOR_COMPLETE_AFTER_CANCEL,
  'transition/auto-complete-after-reschedule-booking',
  'transition/operator-complete-after-reschedule-booking',
  TRANSITIONS.MARK_RECEIVED_FROM_DISPUTED,
]);

const REVIEWED_TRANSITIONS = new Set([
  TRANSITIONS.REVIEW_1_BY_PROVIDER,
  TRANSITIONS.REVIEW_2_BY_PROVIDER,
  TRANSITIONS.REVIEW_1_BY_CUSTOMER,
  TRANSITIONS.REVIEW_2_BY_CUSTOMER,
  'transition/expire-review-period',
  'transition/expire-customer-review-period',
  'transition/expire-provider-review-period',
]);

const DISPUTED_TRANSITIONS = new Set([TRANSITIONS.DISPUTE, TRANSITIONS.OPERATOR_DISPUTE]);

const CANCELLED_UPDATE_TRANSITIONS = new Set([
  TRANSITIONS.INITIATE_REFUND,
  TRANSITIONS.ADD_ACCOUNT_INFO_BY_CUSTOMER,
]);

const RESCHEDULE_DECLINED_TRANSITIONS = new Set([TRANSITIONS.DECLINE_RESCHEDULE_BOOKING]);

const getTransactionStatus = notification => {
  const transitionName = getNotificationTransitionName(notification);
  if (!transitionName) {
    return null;
  }

  const isCustomer = !isProviderNotificationTarget(notification);
  const transaction = notification?.transaction || null;
  const paymentMethod =
    transaction?.attributes?.protectedData?.paymentMethod ||
    transaction?.attributes?.metadata?.paymentMethod ||
    notification?.paymentMethod;

  return getStatusByTransition(transitionName, paymentMethod, isCustomer, transaction).status;
};

const resolveNotificationStatus = notification => {
  const type = String(notification?.type || '');
  const transitionName = getNotificationTransitionName(notification);

  if (type.startsWith('message.')) {
    return null;
  }

  if (isHostPayoutNotification(notification)) {
    return BookingStatus.PAID_OUT;
  }

  switch (type) {
    case 'booking.accepted':
      if (isCreativeAcceptedPendingPaymentNotification(notification)) {
        return BookingStatus.PENDING_PAYMENT;
      }

      return getTransactionStatus(notification) || BookingStatus.CONFIRMED;
    case 'booking.secured':
      return BookingStatus.CONFIRMED;
    case 'booking.rescheduleAccepted':
      return BookingStatus.RESCHEDULE_ACCEPTED;
    case 'booking.paymentReminder':
      return BookingStatus.PENDING_PAYMENT;
    case 'booking.paymentNotConfirmed':
      return BookingStatus.FAILED_PAYMENT;
    case 'booking.requested':
      if (isExpiredRequestNotification(notification)) {
        return BookingStatus.CANCELLED;
      }

      if (transitionName === TRANSITIONS.TRANSITION_INQUIRE) {
        return BookingStatus.INQUIRY;
      }

      return getTransactionStatus(notification) || BookingStatus.PENDING;
    case 'booking.requestExpired':
      return BookingStatus.CANCELLED;
    case 'booking.declined':
      return RESCHEDULE_DECLINED_TRANSITIONS.has(transitionName)
        ? BookingStatus.RESCHEDULE_DECLINED
        : BookingStatus.DECLINED;
    case 'booking.cancelled':
      return BookingStatus.CANCELLED;
    case 'booking.updated':
      if (isPaymentSuccessNotification(notification)) {
        return getTransactionStatus(notification) || BookingStatus.PENDING;
      }

      if (DISPUTED_TRANSITIONS.has(transitionName)) {
        return BookingStatus.DISPUTED;
      }

      if (REVIEWED_TRANSITIONS.has(transitionName)) {
        return BookingStatus.REVIEWED;
      }

      if (CANCELLED_UPDATE_TRANSITIONS.has(transitionName)) {
        return BookingStatus.CANCELLED;
      }

      if (COMPLETED_TRANSITIONS.has(transitionName)) {
        return BookingStatus.COMPLETED;
      }

      return getTransactionStatus(notification) || BookingStatus.COMPLETED;
    default:
      return null;
  }
};

export const getNotificationAppearance = notification => {
  if (isPaymentWindowPassedNotification(notification)) {
    return {
      ...getBookingStatusAppearance(BookingStatus.CANCELLED),
      status: BookingStatus.CANCELLED,
    };
  }

  if (isExpiredRequestNotification(notification)) {
    return {
      ...getBookingStatusAppearance(BookingStatus.CANCELLED),
      status: BookingStatus.CANCELLED,
    };
  }

  const status = resolveNotificationStatus(notification);

  if (!status) {
    return MESSAGE_NOTIFICATION_APPEARANCE;
  }

  const appearance = getBookingStatusAppearance(status);
  if (!appearance) {
    return {
      color: '#5A59E1',
      softColor: '#F2F0FF',
      status,
    };
  }

  return {
    ...appearance,
    status,
  };
};

export const getNotificationIcon = (
  notification,
  appearance = getNotificationAppearance(notification)
) => {
  if (appearance?.status === MESSAGE_NOTIFICATION_APPEARANCE.status) {
    return 'chat';
  }

  if (appearance?.status === BookingStatus.PAID_OUT) {
    return 'dashboard';
  }

  if (CANCELLED_ICON_STATUSES.has(appearance?.status)) {
    return 'cancelled';
  }

  const type = notification?.type;

  switch (type) {
    case 'booking.accepted':
    case 'booking.secured':
    case 'booking.rescheduleAccepted':
    case 'booking.updated':
      return 'reschedule';
    case 'booking.paymentReminder':
    case 'booking.requested':
    case 'booking.rescheduleRequested':
      return 'request';
    default:
      return 'request';
  }
};

export const getNotificationStatus = notification => {
  return getNotificationAppearance(notification).status || null;
};
